import { useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  Announcements,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {
  CSS as DndCSS,
} from '@dnd-kit/utilities';
import { ListCard } from './ListCard';
import { List } from '@/types/lists';
import { cn } from '@/utils/cn';

// Create accessibility announcements for screen readers during keyboard reordering
const getAnnouncements = (lists: List[]): Announcements => ({
  onDragStart({ active }) {
    const item = lists.find(l => l.id === active.id);
    return `Started reordering ${item?.title || 'list'}. Use Up and Down arrow keys to change position.`;
  },
  onDragOver({ active, over }) {
    if (over) {
      const activeItem = lists.find(l => l.id === active.id);
      const overItem = lists.find(l => l.id === over.id);
      const overIndex = lists.findIndex(l => l.id === over.id);
      return `${activeItem?.title || 'List'} is now at position ${overIndex + 1}, above ${overItem?.title || 'list'}.`;
    }
    return undefined;
  },
  onDragEnd({ active, over }) {
    const item = lists.find(l => l.id === active.id);
    if (over && active.id !== over.id) {
      const newIndex = lists.findIndex(l => l.id === over.id) + 1;
      return `Finished reordering. ${item?.title || 'List'} is now priority ${newIndex}.`;
    }
    return `Reordering completed. ${item?.title || 'List'} position unchanged.`;
  },
  onDragCancel({ active }) {
    const item = lists.find(l => l.id === active.id);
    return `Reordering cancelled. ${item?.title || 'List'} returned to original position.`;
  },
});

interface SortableListCardProps {
  list: List;
  onEdit: (list: List) => void;
  onDelete: (list: List) => void;
  onItemClick?: (list: List) => void;
  showDragHandle: boolean;
}

function SortableListCard({
  list,
  onEdit,
  onDelete,
  onItemClick,
  showDragHandle
}: SortableListCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id: list.id });

  const style = {
    transform: DndCSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative group",
        isSorting && "ring-2 ring-primary ring-offset-2"
      )}
    >
      <ListCard
        list={list}
        onEdit={onEdit}
        onDelete={onDelete}
        onItemClick={onItemClick}
        isDragging={isDragging}
        dragHandleProps={showDragHandle ? {
          ...attributes,
          ...listeners,
          'aria-pressed': isDragging,
        } : undefined}
        showPriority={showDragHandle}
        className={cn(
          isDragging && "opacity-50 transform rotate-1 shadow-lg z-50"
        )}
      />
    </div>
  );
}

interface ListsListProps {
  lists: List[];
  onEdit: (list: List) => void;
  onDelete: (list: List) => void;
  onReorder: (lists: List[]) => void;
  onItemClick?: (list: List) => void;
  className?: string;
  showDragHandles?: boolean;
}

export function ListsList({
  lists,
  onEdit,
  onDelete,
  onReorder,
  onItemClick,
  className,
  showDragHandles = false
}: ListsListProps) {
  // Memoize announcements to prevent unnecessary re-renders
  const announcements = useMemo(() => getAnnouncements(lists), [lists]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    console.log('🔄 Lists Drag End Event:', { activeId: active.id, overId: over?.id });

    // Only allow reordering when drag handles are shown (single goal selected)
    if (!showDragHandles) {
      console.log('❌ Drag handles disabled - no reordering allowed');
      return;
    }

    if (over && active.id !== over.id) {
      const oldIndex = lists.findIndex(item => item.id === active.id);
      const newIndex = lists.findIndex(item => item.id === over.id);
      
      console.log('📊 Lists Drag Details:', {
        activeId: active.id,
        overId: over.id,
        oldIndex,
        newIndex,
        originalOrder: lists.map(l => ({ id: l.id, title: l.title, priority: l.priority }))
      });

      // Create visual reorder for immediate UI feedback
      const visuallyReordered = arrayMove(lists, oldIndex, newIndex);
      
      // Recalculate priorities to ensure they're sequential and unique within the goal
      // Since we're only allowing drag/drop within a single goal, all lists have the same goalId
      const finalReordered = visuallyReordered.map((list, index) => ({
        ...list,
        priority: index + 1, // Sequential priorities starting from 1
        updatedAt: new Date()
      }));
      
      const draggedItem = lists[oldIndex];
      console.log('🎯 Lists Priority Recalculation:', {
        draggedItem: draggedItem.title,
        oldPriority: draggedItem.priority,
        oldIndex,
        newIndex,
        goalScope: 'Single goal - sequential priorities'
      });
      
      console.log('🔀 Final Lists Order with Sequential Priorities:', 
        finalReordered.map(l => ({ id: l.id, title: l.title, priority: l.priority }))
      );

      onReorder(finalReordered);
    } else {
      console.log('❌ No lists reorder needed:', { 
        sameItem: active.id === over?.id, 
        noOver: !over 
      });
    }
  };

  if (lists.length === 0) {
    return (
      <div className={cn("text-center py-12", className)}>
        <p className="text-muted-foreground">No lists found matching your criteria.</p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable: 'Press Space to start reordering this list. Use Up and Down arrow keys to move it. Press Space to confirm or Escape to cancel.',
        },
      }}
    >
      <SortableContext
        items={lists.map(l => l.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className={cn("flex flex-col gap-3", className)}>
          {lists.map(list => (
            <SortableListCard
              key={list.id}
              list={list}
              onEdit={onEdit}
              onDelete={onDelete}
              onItemClick={onItemClick}
              showDragHandle={showDragHandles}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}